/**
 * Item coverage for `appliedCharges[]` / `appliedTaxes[]`.
 *
 * The backend no longer auto-applies a charge/tax to every line item — each entry declares
 * its own coverage, and omitting both fields means "applies to no line items" (nothing is
 * derived server-side). Coverage is expressed two ways because custom/addon rows have no
 * item id until the backend mints one mid-transaction:
 *
 *   - `itemIds`     — line items carrying an `itemId` in THIS request
 *   - `itemIndexes` — 0-based positions in THIS request's `items[]` / `lineItems[]`,
 *                     for line items with no `itemId`
 *
 * Per line item: has an `itemId` -> id into `itemIds`; otherwise -> position into
 * `itemIndexes`. Never both. Same rule for create and edit. `itemIndexes` is request-only —
 * responses always come back with resolved `itemIds`.
 */

export interface ItemScope {
  itemIds: string[];
  itemIndexes: number[];
}

/**
 * The only thing the scope builder reads off a line item it is about to send. Documented as
 * a shape rather than used as the generic constraint: it is a weak type (every property
 * optional), so TypeScript would reject the very object literals we most need to handle —
 * custom/addon lines, which omit `itemId` entirely.
 */
export interface ScopableLine {
  itemId?: string | null;
}

/**
 * Build the item coverage for one charge/tax entry.
 *
 * MUST be called with the FINAL array about to be sent — post-filter, post
 * `sortLineItemsAddonLast`, post-`sortOrder`. `itemIndexes` are positions in that exact
 * array, so computing them against the form's row order would mis-target line items.
 *
 * `covers(line, index)` runs once per line item; every covered one lands in exactly one of
 * the two arrays. `itemIds` is deduped defensively — the catalog dropdown already prevents
 * the same item being picked twice, but the helper must not emit duplicates regardless.
 */
export function buildItemScope<T extends object>(
  sentItems: readonly T[],
  covers: (line: T, index: number) => boolean,
): ItemScope {
  const itemIds: string[] = [];
  const itemIndexes: number[] = [];
  const seen = new Set<string>();

  sentItems.forEach((line, index) => {
    if (!covers(line, index)) return;
    const { itemId } = line as ScopableLine;
    if (itemId) {
      if (!seen.has(itemId)) {
        seen.add(itemId);
        itemIds.push(itemId);
      }
    } else {
      itemIndexes.push(index);
    }
  });

  return { itemIds, itemIndexes };
}

/**
 * Spread-ready form of a scope. Empty arrays are dropped, matching how these payloads omit
 * empty keys elsewhere — so an entry covering nothing sends neither field, which is exactly
 * the backend's "applies to no line items".
 */
export function itemScopeFields(scope: ItemScope): { itemIds?: string[]; itemIndexes?: number[] } {
  return {
    ...(scope.itemIds.length ? { itemIds: scope.itemIds } : {}),
    ...(scope.itemIndexes.length ? { itemIndexes: scope.itemIndexes } : {}),
  };
}

/**
 * Coverage predicate for service charges: the charge base is the full discounted subtotal,
 * so a charge covers every line item that is sent.
 */
export const COVERS_ALL = (): boolean => true;
